import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { Upload, X, Trash2 } from "lucide-react";
import {
  useDeleteProductImageMutation,
  useGetProductDetailsQuery,
  useUploadProductImagesMutation,
} from "../../redux/api/productsApi";
import AdminProfile from "../user/AdminProfile";
import { Helmet } from "react-helmet-async";

const ImageUploader = () => {
  const fileInputRef = useRef(null);
  const params = useParams();
  const navigate = useNavigate();

  const [images, setImages] = useState([]);
  const [imagesPreview, setImagesPreview] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);

  const [uploadProductImages, { isLoading, error, isSuccess }] =
    useUploadProductImagesMutation();

  const [
    deleteProductImage,
    { isLoading: isDeleteLoading, error: deleteError },
  ] = useDeleteProductImageMutation();

  const { data } = useGetProductDetailsQuery(params?.id);

  useEffect(() => {
    if (data?.product) {
      setUploadedImages(data?.product?.images);
    }

    if (error) {
      toast.error(error?.data?.message);
    }

    if (deleteError) {
      toast.error(deleteError?.data?.message);
    }

    if (isSuccess) {
      setImagesPreview([]);
      toast.success("Uploaded successfully!");
      navigate("/admin/products");
    }

    if (isSuccess && data?.product) {
      setUploadedImages(data.product.images);
      setImagesPreview([]);
      setImages([]);
    }
  }, [data, error, isSuccess, navigate, deleteError]);

  const onChange = (e) => {
    const files = Array.from(e.target.files);

    files.forEach((file) => {
      const reader = new FileReader();

      reader.onload = () => {
        if (reader.readyState === 2) {
          setImagesPreview((oldArray) => [...oldArray, reader.result]);
          setImages((oldArray) => [...oldArray, reader.result]);
        }
      };

      reader.readAsDataURL(file);
    });
  };

  const handleResetFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleImagePreviewDelete = (img) => {
    const filteredPreview = imagesPreview.filter((i) => i !== img);

    setImages(filteredPreview);
    setImagesPreview(filteredPreview);
  };

  // Upload new images
  const submitHandler = (e) => {
    e.preventDefault();

    if (!images || images.length === 0) {
      toast.error("Please select at least one image before uploading.");
      return;
    }
    uploadProductImages({ id: params?.id, body: { images } }); // images = base64 array
  };

  const deleteImage = (imgId) => {
    deleteProductImage({ id: params?.id, body: { imgId } });
  };

  return (
    <AdminProfile>
       <Helmet>
        <title>Upload Product Images</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="w-full mx-auto bg-white shadow rounded-xl p-6">
        <form
          className="shadow rounded bg-body"
          encType="multipart/form-data"
          onSubmit={submitHandler}
        >
          <h2 className="text-2xl font-semibold text-stone-700 mb-6">
            Upload Product Images
          </h2>

          <div
            className="border-2 border-dashed border-stone-300/60 rounded-xl p-10 
                     flex flex-col items-center justify-center gap-3 
                     bg-[#fdfcf7] hover:border-[#BC3F4A] transition cursor-pointer"
          >
            <Upload className="w-8 h-8 text-stone-400" />
            <p className="text-sm text-stone-500">
              <label htmlFor="customFile"> </label>

              <input
                ref={fileInputRef}
                type="file"
                name="product_images"
                className="text-[#BC3F4A] font-medium cursor-pointer hover:underline"
                id="customFile"
                multiple
                onChange={onChange}
                onClick={handleResetFileInput}
              />
            </p>
          </div>

          {/* New Images Preview */}
          {imagesPreview?.length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-medium text-stone-600 mb-2">
                New Images:
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {imagesPreview?.map((img, idx) => (
                  <div
                    key={idx}
                    className="relative border border-stone-200 rounded-lg overflow-hidden shadow-sm bg-white group"
                  >
                    <img
                      src={img}
                      alt="preview"
                      className="w-full h-32 object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleImagePreviewDelete(img)}
                      className="absolute top-2 right-2 bg-white/90 rounded-full p-1 shadow 
                               opacity-0 group-hover:opacity-100 transition"
                    >
                      <X className="w-4 h-4 text-[#BC3F4A]" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Uploaded Images */}
          {uploadedImages?.length > 0 && (
            <div className="mt-8">
              <p className="text-sm font-medium text-stone-600 mb-2">
                Existing Images:
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {uploadedImages?.map((img, idx) => (
                  <div
                    key={idx}
                    className="relative border border-stone-200 rounded-lg overflow-hidden shadow-sm bg-white group"
                  >
                    <img
                      src={img?.url}
                      alt="uploaded"
                      className="w-full h-32 object-cover"
                    />
                    <button
                      type="button"
                      disabled={isLoading || isDeleteLoading}
                      onClick={() => deleteImage(img?.public_id)}
                      className="absolute top-2 right-2 bg-white/90 rounded-full p-1 shadow 
                               opacity-0 group-hover:opacity-100 transition disabled:opacity-40"
                    >
                      <Trash2 className="w-4 h-4 text-[#BC3F4A]" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            id="register_button"
            type="submit"
            disabled={isLoading || isDeleteLoading}
            className="mt-6 w-full py-2 bg-[#BC3F4A] text-white rounded-lg 
                     font-medium shadow hover:bg-[#a83540] transition disabled:opacity-50"
          >
            {isLoading ? "Uploading..." : "Upload"}
          </button>
        </form>
      </div>
    </AdminProfile>
  );
};

export default ImageUploader;
